import {
  type ChangeEventHandler,
  type ComponentProps,
  type KeyboardEventHandler,
  type MouseEvent,
  type ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Avatar } from 'src/components/Avatar';
import { Box } from 'src/components/Box';
import { List, ListItem } from 'src/components/ListItem';
import { Popover } from 'src/components/Popover';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { Identifier } from 'src/types/utilityTypes';

export interface MentionToken {
  id: Identifier;
  name: string;
}

export interface MentionSearchResult {
  id: Identifier;
  name: string;
  avatar?: string;
}

/**
 * Converts display text with @DisplayName tokens into Mentionable format:
 * @[Display Name](user:123)
 *
 * Longest display names match first; each entry in `mentions` (by array index)
 * encodes at most one @token, in first-occurrence order.
 */
export function encodeMentions(
  display: string,
  mentions: MentionToken[]
): string {
  const indicesByName = new Map<string, number[]>();
  mentions.forEach((m, index) => {
    const list = indicesByName.get(m.name) ?? [];
    list.push(index);
    indicesByName.set(m.name, list);
  });

  const usedIndex = new Set<number>();

  let result = '';
  let i = 0;

  while (i < display.length) {
    const at = display.indexOf('@', i);
    if (at === -1) {
      result += display.slice(i);
      break;
    }
    result += display.slice(i, at);

    const atStart = at === 0 || /\s/.test(display[at - 1]!);
    if (!atStart) {
      result += '@';
      i = at + 1;
      continue;
    }

    const rest = display.slice(at + 1);
    const nameCandidates = [...indicesByName.keys()].sort(
      (a, b) => b.length - a.length
    );

    let matchedIndex: number | null = null;
    let matchedName: string | null = null;

    for (const name of nameCandidates) {
      if (!rest.startsWith(name)) continue;
      const after = rest[name.length];
      // Stop match only if mention text continues as part of a longer word token
      if (after !== undefined && !/\s/.test(after) && /[a-zA-Z0-9_]/.test(after)) {
        continue;
      }
      const candidates = indicesByName.get(name) ?? [];
      const pick = candidates.find((idx) => !usedIndex.has(idx));
      if (pick !== undefined) {
        matchedIndex = pick;
        matchedName = name;
        break;
      }
    }

    if (matchedIndex !== null && matchedName !== null) {
      const m = mentions[matchedIndex]!;
      usedIndex.add(matchedIndex);
      result += `@[${m.name}](user:${m.id})`;
      i = at + 1 + matchedName.length;
    } else {
      result += '@';
      i = at + 1;
    }
  }

  return result;
}

function findActiveMentionQuery(
  value: string,
  cursor: number
): { at: number; query: string } | null {
  let j = cursor - 1;
  while (j >= 0) {
    const ch = value[j]!;
    if (ch === '@') {
      const atStart = j === 0 || /\s/.test(value[j - 1]!);
      if (!atStart) {
        j -= 1;
        continue;
      }
      const query = value.slice(j + 1, cursor);
      if (!/\s/.test(query)) {
        return { at: j, query };
      }
      return null;
    }
    if (ch === ' ' || ch === '\n') {
      return null;
    }
    j -= 1;
  }
  return null;
}

export interface MentionTextFieldProps {
  value: string;
  onChange: (value: string) => void;
  mentions: MentionToken[];
  onMentionsChange: (mentions: MentionToken[]) => void;
  onSearch: (
    query: string,
    signal: AbortSignal
  ) => Promise<MentionSearchResult[]>;
  disabled?: boolean;
  placeholder?: string;
  size?: 'small' | 'medium';
  sx?: ComponentProps<typeof TextField>['sx'];
  multiline?: boolean;
}

export function MentionTextField({
  value,
  onChange,
  mentions,
  onMentionsChange,
  onSearch,
  disabled,
  placeholder,
  size,
  sx,
  multiline = false,
}: MentionTextFieldProps): ReactElement {
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<MentionSearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [activeAt, setActiveAt] = useState<number | null>(null);
  const [activeQuery, setActiveQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const searchAbortRef = useRef<AbortController | null>(null);
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;

  useEffect(() => {
    if (!popoverOpen || activeAt === null) {
      setSuggestions([]);
      setSearchLoading(false);
      return;
    }

    searchAbortRef.current?.abort();
    const ac = new AbortController();
    searchAbortRef.current = ac;
    setSearchLoading(true);

    const run = async () => {
      try {
        const rows = await onSearchRef.current(activeQuery, ac.signal);
        if (!ac.signal.aborted) {
          setSuggestions(rows);
          setSelectedIndex(0);
        }
      } catch {
        if (!ac.signal.aborted) {
          setSuggestions([]);
        }
      } finally {
        if (!ac.signal.aborted) {
          setSearchLoading(false);
        }
      }
    };

    void run();

    return () => {
      ac.abort();
    };
  }, [activeQuery, activeAt, popoverOpen]);

  const updateMentionUiFromDom = () => {
    const el = inputRef.current;
    if (!el) return;
    const cursor =
      'selectionStart' in el && typeof el.selectionStart === 'number'
        ? el.selectionStart
        : value.length;
    const ctx = findActiveMentionQuery(value, cursor);
    if (ctx) {
      setActiveAt(ctx.at);
      setActiveQuery(ctx.query);
      setPopoverOpen(true);
    } else {
      setActiveAt(null);
      setActiveQuery('');
      setPopoverOpen(false);
    }
  };

  const applySuggestion = (item: MentionSearchResult) => {
    const el = inputRef.current;
    if (!el || activeAt === null) return;
    const cursor =
      'selectionStart' in el && typeof el.selectionStart === 'number'
        ? el.selectionStart
        : value.length;

    const before = value.slice(0, activeAt);
    const after = value.slice(cursor);
    const inserted = `@${item.name}`;
    const next = `${before}${inserted}${after}`;
    onChange(next);
    onMentionsChange([...mentions, { id: item.id, name: item.name }]);
    setPopoverOpen(false);
    setActiveAt(null);
    setActiveQuery('');

    const pos = before.length + inserted.length;
    requestAnimationFrame(() => {
      el.focus();
      if ('setSelectionRange' in el) {
        el.setSelectionRange(pos, pos);
      }
    });
  };

  const handleChange: ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
    const cursor = e.target.selectionStart ?? newValue.length;
    const ctx = findActiveMentionQuery(newValue, cursor);
    if (ctx) {
      setActiveAt(ctx.at);
      setActiveQuery(ctx.query);
      setPopoverOpen(true);
    } else {
      setActiveAt(null);
      setActiveQuery('');
      setPopoverOpen(false);
    }
  };

  const handleKeyDown: KeyboardEventHandler<HTMLElement> = (e) => {
    if (popoverOpen && (suggestions.length > 0 || searchLoading)) {
      if (e.key === 'ArrowDown' && suggestions.length > 0) {
        e.preventDefault();
        setSelectedIndex((idx) => (idx + 1) % suggestions.length);
        return;
      }
      if (e.key === 'ArrowUp' && suggestions.length > 0) {
        e.preventDefault();
        setSelectedIndex(
          (idx) => (idx + suggestions.length - 1) % suggestions.length
        );
        return;
      }
      if (e.key === 'Enter' && suggestions.length > 0) {
        e.preventDefault();
        const pick = suggestions[selectedIndex];
        if (pick) applySuggestion(pick);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setPopoverOpen(false);
        setSearchLoading(false);
        return;
      }
    }

    if (e.key === 'Backspace' && !e.shiftKey) {
      const el = inputRef.current;
      if (!el || !('selectionStart' in el)) return;
      const start = el.selectionStart ?? 0;
      const end = el.selectionEnd ?? 0;
      if (start !== end) return;

      const sortedMentions = [...mentions].sort(
        (a, b) => b.name.length - a.name.length
      );
      for (const m of sortedMentions) {
        const suffix = `@${m.name}`;
        if (value.slice(0, start).endsWith(suffix)) {
          e.preventDefault();
          const cut = value.slice(0, start - suffix.length) + value.slice(start);
          onChange(cut);
          onMentionsChange(
            (() => {
              const idx = [...mentions]
                .map((x, i) => ({ x, i }))
                .reverse()
                .find(
                  ({ x }) =>
                    String(x.id) === String(m.id) && x.name === m.name
                )?.i;
              if (idx === undefined) return mentions;
              return mentions.filter((_, i) => i !== idx);
            })()
          );
          requestAnimationFrame(() => {
            const pos = start - suffix.length;
            el.focus();
            if ('setSelectionRange' in el) {
              el.setSelectionRange(pos, pos);
            }
            updateMentionUiFromDom();
          });
          return;
        }
      }
    }
  };

  return (
    <Box ref={anchorRef} sx={[{ position: 'relative', flex: 1 }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}>
      <TextField
        inputRef={inputRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        InputProps={{
          onSelect: updateMentionUiFromDom,
          onClick: updateMentionUiFromDom,
        }}
        onBlur={() => {
          setTimeout(() => setPopoverOpen(false), 150);
        }}
        disabled={disabled}
        placeholder={placeholder}
        size={size}
        multiline={multiline}
        fullWidth
      />
      <Popover
        open={
          popoverOpen &&
          activeAt !== null &&
          (suggestions.length > 0 || searchLoading)
        }
        anchorEl={anchorRef.current}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        hideBackdrop
        slotProps={{
          paper: {
            onMouseDown: (ev: MouseEvent) => {
              ev.preventDefault();
            },
            sx: { maxHeight: 220, minWidth: 240, mt: 0.5 },
          },
        }}
      >
        {searchLoading && suggestions.length === 0 ? (
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              …
            </Typography>
          </Box>
        ) : (
          <List dense sx={{ py: 0 }}>
            {suggestions.map((row, index) => (
              <ListItem
                key={String(row.id)}
                clickable
                selected={index === selectedIndex}
                primary={row.name}
                avatar={
                  <Avatar src={row.avatar} sx={{ width: 32, height: 32 }} />
                }
                onClick={() => applySuggestion(row)}
              />
            ))}
          </List>
        )}
      </Popover>
    </Box>
  );
}
